import { useState } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Sélecteur de catégorie comptable avec ajout/suppression de catégories.
 *
 * Props:
 * - value, onChange: contrôle la valeur (string)
 * - type: 'charge' | 'produit'
 * - defaultCategories: liste des catégories par défaut SYSCOHADA
 * - hiddenDefaults: liste des défauts masqués par l'utilisateur (filtre côté affichage)
 * - customCategories: liste des catégories ajoutées par l'utilisateur
 * - onCategoriesChange(newCustomList, newHiddenDefaults): callback après ajout/suppression
 */
export default function CategorySelect({
  value,
  onChange,
  type,
  defaultCategories = [],
  hiddenDefaults = [],
  customCategories = [],
  onCategoriesChange,
  className = '',
  testId,
  triggerClassName = '',
}) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { name, isDefault }
  const [deleting, setDeleting] = useState(false);

  const visibleDefaults = defaultCategories.filter((c) => !hiddenDefaults.includes(c));
  const allCategories = [...visibleDefaults, ...customCategories];

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error('Nom requis');
      return;
    }
    if (allCategories.includes(trimmed)) {
      toast.error('Cette catégorie existe déjà');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/financial/categories`, {
        type,
        name: trimmed,
      });
      const updated = res.data?.categories || {};
      const updatedHidden = res.data?.hiddenDefaults || {};
      const typeToBucket = { charge: 'charges', produit: 'produits', actif: 'actifs', passif: 'passifs' };
      const bucket = typeToBucket[type] || 'charges';
      onCategoriesChange?.(updated[bucket] || [], updatedHidden[bucket] || []);
      onChange?.(trimmed);
      toast.success('Catégorie ajoutée');
      setShowAddModal(false);
      setNewName('');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Erreur à l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const { name, isDefault } = pendingDelete;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API}/api/financial/categories/${type}/${encodeURIComponent(name)}?is_default=${isDefault ? 'true' : 'false'}`
      );
      const updated = res.data?.categories || {};
      const updatedHidden = res.data?.hiddenDefaults || {};
      const typeToBucket = { charge: 'charges', produit: 'produits', actif: 'actifs', passif: 'passifs' };
      const bucket = typeToBucket[type] || 'charges';
      onCategoriesChange?.(updated[bucket] || [], updatedHidden[bucket] || []);
      // Si la valeur courante a été supprimée, basculer sur la première dispo
      if (value === name) {
        const newDefaults = (defaultCategories || []).filter((c) => !(updatedHidden[bucket] || []).includes(c));
        const fallback = newDefaults[0] || (updated[bucket] || [])[0] || '';
        onChange?.(fallback);
      }
      toast.success('Catégorie supprimée');
      setPendingDelete(null);
    } catch (err) {
      console.error(err);
      toast.error('Erreur à la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const requestDelete = (e, name, isDefault) => {
    e.preventDefault();
    e.stopPropagation();
    setPendingDelete({ name, isDefault });
  };

  const restoreDefaults = async () => {
    try {
      const res = await axios.post(`${API}/api/financial/categories/restore-defaults`, { type });
      const updated = res.data?.categories || {};
      const updatedHidden = res.data?.hiddenDefaults || {};
      const bucket = ({ charge: 'charges', produit: 'produits', actif: 'actifs', passif: 'passifs' })[type] || 'charges';
      onCategoriesChange?.(updated[bucket] || [], updatedHidden[bucket] || []);
      const restoredCount = res.data?.restored_count || 0;
      if (restoredCount > 0) {
        toast.success(`${restoredCount} catégorie(s) restaurée(s)`);
      } else {
        toast.info('Aucune catégorie à restaurer');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la restauration');
    }
  };

  return (
    <>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={triggerClassName} data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className={className}>
          {visibleDefaults.map((cat) => (
            <div key={`def-${cat}`} className="flex items-center group">
              <SelectItem value={cat} className="flex-1">
                {cat}
              </SelectItem>
              <button
                type="button"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  requestDelete(e, cat, true);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="mr-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded transition shrink-0"
                title={`Masquer "${cat}"`}
                data-testid={`delete-cat-${cat}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {customCategories.length > 0 && (
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-gray-400 border-t border-b mt-1">
              Mes catégories
            </div>
          )}
          {customCategories.map((cat) => (
            <div key={`cust-${cat}`} className="flex items-center group">
              <SelectItem value={cat} className="flex-1">
                {cat}
              </SelectItem>
              <button
                type="button"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  requestDelete(e, cat, false);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="mr-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded transition shrink-0"
                title={`Supprimer "${cat}"`}
                data-testid={`delete-custom-cat-${cat}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="border-t mt-1 pt-1">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAddModal(true);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded font-medium"
              data-testid={`add-category-btn-${type}`}
            >
              <Plus className="h-4 w-4" />
              Ajouter une catégorie
            </button>
          </div>
        </SelectContent>
      </Select>

      {/* Modal d'ajout */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md" data-testid="add-category-modal">
          <DialogHeader>
            <DialogTitle>
              Nouvelle catégorie {type === 'charge' ? 'de charge' : 'de produit'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Nom de la catégorie *</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={type === 'charge' ? 'Ex: Frais de douane' : 'Ex: Sponsoring événement'}
                className="mt-1"
                autoFocus
                data-testid="new-category-input"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
            </div>
            <p className="text-xs text-gray-500">
              Cette catégorie sera disponible immédiatement pour toutes les saisies.
            </p>
            <div className="border-t pt-3 mt-2 flex items-center justify-between gap-2 bg-amber-50 -mx-6 px-6 py-3">
              <div className="text-xs text-amber-900">
                {hiddenDefaults.length > 0 ? (
                  <>
                    <span className="font-semibold">{hiddenDefaults.length}</span> catégorie(s) par défaut masquée(s)
                  </>
                ) : (
                  <>Aucune catégorie masquée pour l'instant</>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={restoreDefaults}
                disabled={hiddenDefaults.length === 0}
                className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                data-testid="restore-defaults-btn"
                title="Restaurer toutes les catégories par défaut supprimées"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Restaurer les défauts
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} disabled={saving}>
              Annuler
            </Button>
            <Button
              onClick={handleAdd}
              disabled={saving || !newName.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 gap-2"
              data-testid="confirm-add-category-btn"
            >
              <Plus className="h-4 w-4" /> Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmation de suppression */}
      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-cat-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Supprimer la catégorie
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-gray-700">
              Voulez-vous vraiment supprimer la catégorie{' '}
              <span className="font-semibold text-gray-900">"{pendingDelete?.name}"</span> ?
            </p>
            {pendingDelete?.isDefault && (
              <p className="text-xs text-gray-500 mt-2">
                Astuce : vous pourrez la restaurer en l'ajoutant à nouveau via "+ Ajouter une catégorie".
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={deleting}>
              Annuler
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
              data-testid="confirm-delete-cat-btn"
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
